import { Button, Callout, Dialog, Intent } from '@blueprintjs/core'
import * as React from 'react'
import Client from '../../../client'
import APIErrorCallout from '../../../components/errors/APIErrorCallout'
import { showToast } from '../../../Toast'
import { S, readQueryParams } from '../../../utils'
import { trackNeedsAttentionListingEvent } from '../../home/v2/utils'

import {
  AuctionDuration,
  AuctionStart,
  BidIncrement,
  BuyNowPrice,
  ShippingPrice,
  StartingBid
} from '../../products_v4/listings/shopgoodwill/Inputs'

interface Props {
  isOpen: boolean
  data: any // Replace with the actual type
  recycledProductId?: any
  hasSold?: boolean
  actionTitle?: string

  onProductRestored?: (product: any) => void // Replace with the actual type

  onClose: (didSomething?: boolean, failed?: any) => void // Replace with the actual type
  onSubmit?: (form: any, callback: (error: any) => void) => void // Replace with the actual type
}

interface State {
  form: any // Replace with the actual type
  loading: boolean
  error: any // Replace with the actual type
}

export default class RelistDialog extends React.Component<Props, State> {
  state = {
    loading: false,
    error: null,
    form: {
      auctionDuration: '',
      auctionStart: null,
      price: '',
      listingType: '',
      shippingPrice: '',
      bidIncrement: '',
      startingBid: '',
      buyNowPrice: ''
    }
  }

  componentDidMount() {
    this.fetchDefaults()
  }

  onFormChange = (k, v) => {
    this.setState(s => ({
      form: {
        ...s.form,
        [k]: v
      }
    }))
  }

  fetchDefaults = async () => {
    this.setState({ loading: true })
    const res = await Client.getShopgoodwillSettings()
    const { settings } = res.data
    this.setState({
      loading: false,
      form: {
        auctionDuration: settings.defaultAuctionDuration,
        auctionStart: settings.defaultAuctionStart,
        bidIncrement: settings.defaultBidIncrement,
        buyNowPrice: undefined,
        handlingPrice: settings.defaultHandlingPrice,
        itemFeatured: false,
        itemGallery: false,
        reservePrice: undefined,
        startingBid: settings.defaultStartingPrice,
        shippingPrice: settings.defaultShippingPrice
      }
    })
  }

  restoreProduct = async () => {
    const { data, recycledProductId } = this.props
    try {
      await Client.recycleProduct(recycledProductId, false)
      // This callback isn't meant to be called in bulk, therefor assume an array of 1 will be supplied
      if (this.props.onProductRestored) this.props.onProductRestored(data[0])
    } catch (error) {
      this.setState({ loading: false, error: error.response })
    }
  }

  relist = async () => {
    const { data } = this.props
    if (readQueryParams().tab === 'attention-required') trackNeedsAttentionListingEvent('re-list', data.length)
    this.setState({ loading: true, error: null })

    try {
      const form = this.prepareForm()
      const res = await Client.bulkRelistShopgoodwill(data, form)
      this.setState({ loading: false }, () => {
        showToast(`Relisting ${data.length} item${S(data)}...`, Intent.PRIMARY)
        this.props.onClose(true, res.data.failed)
      })
    } catch (error) {
      this.setState({
        loading: false,
        error: error.response
      })
    }
  }

  maybeCustomRelist = async () => {
    if (this.props.onSubmit) {
      this.setState({ loading: true, error: null })
      this.props.onSubmit(this.prepareForm(), error => {
        this.setState({ loading: false, error })
        if (!error) this.props.onClose(true)
      })
    } else {
      this.relist()
    }
  }

  prepareForm = () => {
    const { form } = this.state
    Object.keys(form).forEach(key => {
      if (form[key] === '') form[key] = null
    })
    return form
  }

  onClose = () => {
    this.setState(
      {
        loading: false,
        error: null
      },
      this.props.onClose
    )
  }

  render() {
    const { isOpen, data, recycledProductId } = this.props
    const { form, loading, error } = this.state

    return (
      <Dialog isOpen={isOpen} onClose={this.onClose} title={this.props.actionTitle || 'Relist'}>
        {error && <APIErrorCallout response={error} />}
        {recycledProductId && (
          <Callout intent={Intent.DANGER}>
            This listing&apos;s product is <strong>recycled</strong>. You must restore it first.
            <br />
            <Button style={{ marginTop: 5 }} small intent={Intent.PRIMARY} onClick={this.restoreProduct}>
              Restore
            </Button>
          </Callout>
        )}
        {this.props.hasSold && (
          <Callout intent={Intent.WARNING}>
            You have selected a <strong>Sold</strong> listing. Please check to make sure this was intentional
          </Callout>
        )}
        <div className="bp3-dialog-body">
          <p style={{ marginBottom: 20 }}>Leave fields blank to use current values</p>
          <StartingBid
            placeholder="Use current"
            onChange={v => this.onFormChange('startingBid', v)}
            value={form.startingBid}
          />
          <BidIncrement
            placeholder="Use current"
            onChange={v => this.onFormChange('bidIncrement', v)}
            value={form.bidIncrement}
          />
          <BuyNowPrice
            placeholder="Use current"
            onChange={v => {
              if (parseInt(v) === 0 && Number(form.auctionDuration) > 15) {
                this.setState(s => ({ form: { ...s.form, buyNowPrice: v, auctionDuration: null } }))
              } else {
                this.onFormChange('buyNowPrice', v)
              }
            }}
            value={form.buyNowPrice}
          />
          <ShippingPrice
            allowNull
            placeholder="Use current"
            onChange={v => this.onFormChange('shippingPrice', v)}
            value={form.shippingPrice}
          />
          <AuctionDuration
            buyNowPrice={Number(form.buyNowPrice)}
            placeholder="Use current"
            clearable
            onChange={v => this.onFormChange('auctionDuration', v)}
            value={Number(form.auctionDuration)}
          />
          <AuctionStart
            value={form.auctionStart}
            clearable
            placeholder="Use current"
            onChange={v => this.onFormChange('auctionStart', v)}
          />
        </div>

        <div className="bp3-dialog-footer">
          <div className="bp3-dialog-footer-actions">
            <Button
              loading={loading}
              intent={Intent.PRIMARY}
              onClick={this.maybeCustomRelist}
              disabled={recycledProductId}
            >
              {this.props.actionTitle || 'Relist'} {data.length} item{S(data)}
            </Button>
          </div>
        </div>
      </Dialog>
    )
  }
}
